import React from 'react';
import {
  RiTimeLine,
  RiUserLine,
  RiBuildingLine,
  RiStore2Line,
  RiUserReceivedLine,
} from '@remixicon/react';

import { Badge, type BadgeProps } from '@/design-system/badge';
import {
  AgingTier,
  INVOICE_STATUS_LABELS,
  InvoiceStatus,
} from '@/services/api/invoices';

type BadgeStatus = NonNullable<BadgeProps['status']>;

const STATUS_TONE: Record<InvoiceStatus, BadgeStatus> = {
  PENDING_COLLECTION: 'away',
  RECEIVED_AT_HUB: 'success',
  RECEIVED_BY_RIDER: 'success',
  WAITING_CONFIRMATION: 'verified',
  DISPUTED: 'highlighted',
  RESOLVED: 'success',
  EXCEPTION: 'error',
  COLLECTED: 'information',
  SUBMITTED_TO_CLIENT: 'information',
  CLIENT_ACKNOWLEDGED: 'success',
};

export const InvoiceStatusPill: React.FC<{ status: InvoiceStatus }> = ({
  status,
}) => {
  const tone = STATUS_TONE[status] ?? 'away';
  return (
    <Badge status={tone} type="dot">
      {INVOICE_STATUS_LABELS[status]}
    </Badge>
  );
};

// "Posisi Invoice" — the physical position of the surat jalan. This is a
// SEPARATE axis from the lifecycle Status (a doc can be "Masih di Rider"
// physically while its status is "Exception"). Mirrors the prototype's
// "Where is it" chip: a soft-tint pill with a leading position icon.
interface PositionConfig {
  label: string;
  tone: BadgeStatus;
  Icon: React.ElementType;
}

const INVOICE_POSITION: Record<InvoiceStatus, PositionConfig> = {
  PENDING_COLLECTION: { label: 'Belum Scan', tone: 'neutral', Icon: RiTimeLine },
  RECEIVED_BY_RIDER: { label: 'Masih di Rider', tone: 'away', Icon: RiUserLine },
  RECEIVED_AT_HUB: { label: 'Di Hub', tone: 'information', Icon: RiBuildingLine },
  EXCEPTION: { label: 'Di Apotek', tone: 'warning', Icon: RiStore2Line },
  WAITING_CONFIRMATION: {
    label: 'Di Client',
    tone: 'feature',
    Icon: RiUserReceivedLine,
  },
  DISPUTED: { label: 'Di Client', tone: 'feature', Icon: RiUserReceivedLine },
  RESOLVED: { label: 'Di Client', tone: 'feature', Icon: RiUserReceivedLine },
  // Statuses outside the board's canonical lifecycle — mapped for completeness.
  COLLECTED: { label: 'Masih di Rider', tone: 'away', Icon: RiUserLine },
  SUBMITTED_TO_CLIENT: {
    label: 'Di Client',
    tone: 'feature',
    Icon: RiUserReceivedLine,
  },
  CLIENT_ACKNOWLEDGED: {
    label: 'Di Client',
    tone: 'feature',
    Icon: RiUserReceivedLine,
  },
};

export const InvoicePositionBadge: React.FC<{ status: InvoiceStatus }> = ({
  status,
}) => {
  const config = INVOICE_POSITION[status] ?? INVOICE_POSITION.PENDING_COLLECTION;
  const { label, tone, Icon } = config;
  return (
    <Badge status={tone} appearance="lighter" type="left-icon" icon={<Icon />}>
      {label}
    </Badge>
  );
};

// Aging is *numeric meta*, not a status. Render as plain tabular text with a
// leading colored dot so it doesn't compete with the Status pill for
// attention. Per row, only one filled pill (Status); aging recedes.
const AGING_DOT: Record<AgingTier, string> = {
  green: 'bg-(--state-success-base)',
  amber: 'bg-(--state-warning-base)',
  orange: 'bg-(--state-warning-base)',
  red: 'bg-(--state-error-base)',
};

const AGING_TEXT: Record<AgingTier, string> = {
  green: 'text-text-sub-600',
  amber: 'text-text-sub-600',
  orange: 'text-text-strong-950 font-semibold',
  red: 'text-error-base font-bold',
};

export const AgingBadge: React.FC<{
  tier: AgingTier | null;
  daysSince: number | null;
}> = ({ tier, daysSince }) => {
  if (!tier || daysSince === null) {
    return <span className="text-xs text-text-soft-400">—</span>;
  }
  const label = daysSince === 0 ? 'Same day' : `${daysSince}d`;

  return (
    <span
      className={`inline-flex items-center gap-1.5 text-[12px] tabular-nums tracking-tight ${AGING_TEXT[tier]}`}
    >
      <span className={`relative inline-flex h-1.5 w-1.5`}>
        {tier === 'red' && (
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-(--state-error-base) opacity-75 motion-reduce:animate-none" />
        )}
        <span
          className={`relative inline-flex h-1.5 w-1.5 rounded-full ${AGING_DOT[tier]}`}
        />
      </span>
      {label}
    </span>
  );
};
